import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { Avatar } from 'primereact/avatar';
import { AvatarGroup } from 'primereact/avatargroup';
import { Checkbox } from 'primereact/checkbox';
import { classNames } from 'primereact/utils';
import { TieredMenu } from 'primereact/tieredmenu';

import { handleDateTimeOffset, isTaskOverdue } from 'utils/utility_functions/timezone';
import { useDraggableCard } from '../../../hooks/dragDrop';
import { useDroppableCard } from '../../../hooks/dragDrop';
import { generateFullName } from 'utils/generateFullNameUtils';
import { GroupUserTooltip } from 'components/UI/GroupUserTooltip/GroupUserTooltip';
import { formatStageName, getTenantDetails } from 'utils/utils';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useBoardData, useBoardActions } from '../../../context/BoardContext';
import { useBoardSection } from '../../../context/BoardSectionContext';
import PriorityDropdown from 'components/UI/PriorityDropdown/PriorityDropdown';
import ImageComponent from 'shared/ImageComponent';
import GlobalTooltip from 'components/GlobalTooltip';
import constants from 'constants/index';
import { logger } from '../../../utils/logger';
import { TASK_STAGES } from '../../../utils/constants';

// Store toggle state outside component to persist across rerenders
const subtaskToggleState = new Map();

// Constant styles to prevent recreating objects on every render
const AVATAR_STYLE = { backgroundColor: '#2196F3', color: '#ffffff' };
const OVERFLOW_AVATAR_STYLE = {
  backgroundColor: '#9c27b0',
  color: '#ffffff',
  width: 25,
  height: 25,
};

const TaskCard = ({ item, columnId, index, onDrop, setCardHeight, isLocked = false }) => {
  const [tenantId] = getTenantDetails();
  const { taskStages } = useBoardData();
  const boardActions = useBoardActions();
  const { isFromCase, isFromLead } = useBoardSection();
  const cardContentRef = useRef(null);
  const menu = useRef(null);
  const userContext = useUserDetailsContext();

  // Destructure actions inside useMemo/useCallback to prevent breaking memoization
  const { moveCard, updateCard, updateSubtask } = boardActions;

  const doneStage = useMemo(() => {
    return taskStages?.find((stage) => stage.task_stage_name?.toLowerCase() === 'done');
  }, [taskStages]);

  // Get initial state from Map (persists across rerenders)
  const [showSubTasks, setShowSubTasks] = React.useState(() => subtaskToggleState.get(item.task_id) ?? false);

  // Disable dragging if card is locked
  const { elementRef, isDragging } = useDraggableCard({
    cardId: item.task_id,
    columnId,
    index,
    canDrag: !isLocked, // Disable drag if locked
  });

  const { isOver, edge } = useDroppableCard({
    elementRef,
    cardId: item.task_id,
    columnId,
    onDrop,
  });

  // Sync state from Map when task_id changes (in case card is reused)
  useEffect(() => {
    const savedState = subtaskToggleState.get(item.task_id);
    if (savedState !== undefined && savedState !== showSubTasks) {
      setShowSubTasks(savedState);
    }
  }, [item.task_id]);

  useEffect(() => {
    const measureHeight = () => {
      if (cardContentRef?.current?.clientHeight) {
        setCardHeight(item.task_id, cardContentRef.current.clientHeight);
      }
    };
    const rafId = requestAnimationFrame(() => {
      measureHeight();
    });
    return () => cancelAnimationFrame(rafId);
  }, [item.task_id, setCardHeight, showSubTasks]); // Re-measure when subtasks toggled

  const markAsComplete = useCallback(async () => {
    if (isLocked) {
      logger.warn('Task is currently syncing. Operation rejected.');
      return;
    }

    // Check if already completed
    if (item.task_status === 'completed' || item._task_stage_ === TASK_STAGES.DONE) {
      logger.debug('Task is already completed');
      return;
    }

    try {
      // Find the Done stage using the reusable helper

      if (!doneStage) {
        logger.error('Done stage not found');
        return;
      }

      // Check if task has incomplete subtasks
      const hasIncompleteSubtasks = item?.sub_task?.some((subTask) => !subTask.sub_task_status);

      if (hasIncompleteSubtasks) {
        // TODO: Show dialog asking if user wants to complete all subtasks
        // For now, we'll just complete the subtasks automatically
        logger.warn('Task has incomplete subtasks. Complete subtasks first.');

        // Option 1: Auto-complete all subtasks (uncomment if desired)
        // await handleSubTaskCheckBox(true, item.sub_task.map(st => st.task_id), item);

        // Option 2: Just move to done anyway (current behavior)
        // Continue with completion...
      }
      await moveCard({
        sourceCardId: item.task_id,
        sourceColumnId: columnId,
        targetColumnId: doneStage.task_stage_id,
      });
    } catch (error) {
      logger.error('Failed to mark task as complete:', error);
    }
  }, [isLocked, item.task_id, item.task_status, item._task_stage_, item.pk, item.sub_task, columnId, moveCard, doneStage]);

  const toggleList = useCallback(() => {
    const newState = !showSubTasks;
    setShowSubTasks(newState);
    // Persist state across rerenders
    subtaskToggleState.set(item.task_id, newState);
  }, [showSubTasks, item.task_id]);

  const handleSubTaskCheckBox = useCallback(
    async (checked, subTaskIndex) => {
      if (isLocked) {
        logger.warn('Task is currently syncing. Operation rejected.');
        return;
      }

      try {
        await updateSubtask(item.task_id, subTaskIndex, checked, item, columnId);
      } catch (error) {
        logger.error('Failed to update subtask:', error);
      }
    },
    [isLocked, item, columnId, updateSubtask]
  );

  // Memoize subtask stats to avoid recalculating on every render
  const subtaskStats = useMemo(() => {
    const total = item?.sub_task?.length || 0;
    const completed = item?.sub_task?.filter((st) => st.sub_task_status === true).length || 0;
    return { total, completed };
  }, [item?.sub_task]);

  const handleMoveToStage = useCallback(
    async (targetStageId) => {
      if (isLocked) {
        logger.warn('Task is currently syncing. Operation rejected.');
        return;
      }

      if (targetStageId === columnId) {
        logger.warn('Task is already in this stage.');
        return;
      }

      try {
        await moveCard({
          sourceCardId: item.task_id,
          sourceColumnId: columnId,
          targetColumnId: targetStageId,
        });
      } catch (error) {
        logger.error('Failed to move task:', error);
      }
    },
    [isLocked, columnId, moveCard, item.task_id]
  );

  const handlePriorityChange = useCallback(
    async (newPriority) => {
      if (isLocked) {
        logger.warn('Task is currently syncing. Operation rejected.');
        return;
      }

      if (newPriority === item.priority) {
        // No change, skip update
        return;
      }

      try {
        await updateCard(item.task_id, columnId, {
          priority: newPriority,
          sk: `task#${item.task_id}`,
          pk: `${tenantId}#${item.task_id}`,
          due_date: item?.due_date,
          assign_to: item?.assign_to,
          tast_id: item?.task_id,
          task_stage: item?.task_stage,
        });
      } catch (error) {
        logger.error('Failed to update task priority:', error);
      }
    },
    [isLocked, item.task_id, item.priority, columnId, updateCard, tenantId]
  );

  const menuItems = useMemo(
    () => [
      {
        label: item?.task_stage === doneStage?.task_stage_id ? 'Completed' : 'Mark as complete',
        show: true,
        command: () => markAsComplete(),
        disabled: isLocked || item?.task_stage === doneStage?.task_stage_id,
      },
      {
        label: 'Move To',
        show: true,
        disabled: isLocked,
        items: taskStages
          ?.filter((stage) => stage.task_stage_id !== columnId)
          ?.map((stage) => ({
            label: formatStageName(stage.task_stage_name),
            command: () => handleMoveToStage(stage.task_stage_id),
            disabled: isLocked,
          })),
      },
    ],
    [item, taskStages, columnId, isLocked, handleMoveToStage, markAsComplete, doneStage?.task_stage_id]
  );

  return (
    <div className="item-wrapper" ref={cardContentRef} style={{ marginBottom: '0' }}>
      <div
        ref={elementRef}
        className={`item ${isDragging ? 'is-dragging' : ''} ${isLocked ? 'syncing' : ''}`}
        style={{
          position: 'relative',
          opacity: isDragging ? 0.9 : isLocked ? 0.7 : 1,
          cursor: isLocked ? 'not-allowed' : 'grab',
        }}
        title={isLocked ? '⏳ Syncing... Please wait' : ''}
      >
        {/* Drop indicators */}
        {edge === 'top' && isOver && (
          <div
            style={{
              position: 'absolute',
              top: '-6px',
              left: '8px',
              right: '8px',
              height: '6px',
              background: 'linear-gradient(90deg, #0052CC 0%, #0066FF 100%)',
              borderRadius: '3px',
              zIndex: 1001,
              boxShadow: '0 0 12px rgba(0, 82, 204, 0.9), 0 0 4px rgba(0, 82, 204, 1)',
              animation: 'pulse-card-indicator 1s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />
        )}
        {edge === 'bottom' && isOver && (
          <div
            style={{
              position: 'absolute',
              bottom: '-6px',
              left: '8px',
              right: '8px',
              height: '6px',
              background: 'linear-gradient(90deg, #0052CC 0%, #0066FF 100%)',
              borderRadius: '3px',
              zIndex: 1001,
              boxShadow: '0 0 12px rgba(0, 82, 204, 0.9), 0 0 4px rgba(0, 82, 204, 1)',
              animation: 'pulse-card-indicator 1s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />
        )}

        <div className="container w-100 card-layout card-bg p-3 task-card">
          <div className="d-flex align-items-center pb-2 lh-1 task-border">
            <div className="">
              <GlobalTooltip tooltip={item.task_stage === doneStage?.task_stage_id ? 'Task Completed' : 'Mark as complete'}>
                <i
                  className={classNames({
                    'fas fa-check-circle mark-complete-icon': true,
                    'mark-as-completed': item?.task_stage === doneStage?.task_stage_id,
                  })}
                  onClick={() => !isLocked && markAsComplete()}
                  style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                />
              </GlobalTooltip>
            </div>

            <Link
              className="task-title text-bold pointer ellipsis-1 redirection-link-no-text-transform mb-0"
              title={item.task_name}
              to={
                isFromCase
                  ? `/cases/${item?.case_id}/tasks?id=${item.task_id}`
                  : isFromLead
                  ? `/leads/${item?.case_id}/task?id=${item.task_id}`
                  : `/tasks?id=${item.task_id}`
              }
            >
              {item.task_name}
            </Link>

            <div className="d-flex justify-content-end text-end ms-auto">
              <PriorityDropdown
                className="w-100 border rounded priority-dropdown align-items-center"
                value={item.priority}
                onChange={(e) => handlePriorityChange(e.value)}
                iconOnly
                style={{ height: 30 }}
                disabled={isLocked}
              />
            </div>
            <div
              className="d-flex border border-1 rounded  justify-content-center p-2 task-lane-header-options pointer ms-2"
              onClick={(e) => menu.current.toggle(e)}
            >
              <i className="pi pi-ellipsis-v d-flex align-items-center"></i>
              <TieredMenu model={menuItems} popup ref={menu} id="popup_menu" />
            </div>
          </div>
          {!(isFromCase || isFromLead) && (
            <div className="d-flex align-items-center justify-content-between py-2">
              {item?.case_name && item?.case_id ? (
                <Link
                  className={`text-break text-truncate text-nowrap text-end pointer text-primary-dark redirection-link-no-text-transform`}
                  title={item?.case_name}
                  to={`/${item?.is_lead ? 'leads' : 'cases'}/${item?.case_id || ''}`}
                >
                  {item?.case_name ? item.case_name : 'N/A'}
                </Link>
              ) : (
                <div className={`text-break text-end`}>{'N/A'}</div>
              )}
            </div>
          )}
          <div className="d-flex align-items-center justify-content-between py-2">
            <div className="col-8 d-flex align-items-center task-sub-head">
              <i className="icon-calendar pe-1" />
              Due Date
            </div>
            <div className="col-4">
              <div className="taskcreate-date d-flex justify-content-end">
                <span className="d-flex">
                  {handleDateTimeOffset(userContext?.userDetails?.timezone, item?.due_date, constants.month_date_year_format)}
                </span>
              </div>
            </div>
          </div>
          <div className="date-wrap d-flex align-items-center py-2">
            <div className="col-8 d-flex align-items-center task-sub-head">
              <i className="icon-contacts pe-1" /> Assignees
            </div>
            <div className="col-4 d-flex justify-content-end task-wrap">
              <AvatarGroup>
                {item?.assign_to?.map((user, i) => {
                  if (i < 4) {
                    // Use stable ID instead of generating new UUID on every render
                    const stableId = `${item.task_id}-${user?.assignee_id || user?.group_id || i}`;
                    return (
                      <ImageComponent
                        key={stableId}
                        filePath={
                          user?.contact_id || user?.firm_user_id
                            ? `${tenantId}/${user?.contact_id ? user?.contact_id : user?.firm_user_id}/profile_image`
                            : null
                        }
                        fileName={user.first_name || user?.assignee_name || user?.group_name}
                        className={`me-1 task-avatar assignee-${stableId}`}
                        style={AVATAR_STYLE}
                        toolTipTarget={`.assignee-${stableId}`}
                        toolTipContent={
                          <>
                            {user?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                            {generateFullName(user) || user?.assignee_name || GroupUserTooltip(user, item?.assign_to)}
                          </>
                        }
                        tooltipPosition="top"
                        showTooltip={true}
                      />
                    );
                  }
                  return null;
                })}
                {item?.assign_to?.length > 4 && (
                  <Avatar label={`+${item?.assign_to?.length - 4}`} className="me-1 pointer" style={OVERFLOW_AVATAR_STYLE} shape="circle" />
                )}
              </AvatarGroup>
            </div>
          </div>
          <div className="d-flex py-2">
            <div className="col-12 d-flex justify-content-between">
              <div className="small pointer d-flex align-items-center" onClick={toggleList}>
                Subtasks
                <span className="black-600 ms-2">{` (${subtaskStats.completed}/${subtaskStats.total}) `}</span>
                {showSubTasks ? <i className="pi pi-angle-up ms-2" /> : <i className="pi pi-angle-down ms-2" />}
              </div>
              {isTaskOverdue(item?.due_date, userContext?.userDetails?.timezone) && item._task_stage_ !== TASK_STAGES.DONE && (
                <span className="tag m-1 ms-0 overdue-tag">Overdue</span>
              )}
            </div>
          </div>
          <div className="d-flex flex-column">
            {showSubTasks &&
              item?.sub_task?.map((subItem, index) => {
                return (
                  <div className="col-12 d-flex justify-content-between border sub-tasks p-2 my-1" key={index}>
                    <div className="d-flex align-items-center  F-size12" key={index} style={{ overflow: 'auto' }}>
                      <Checkbox
                        checked={subItem.sub_task_status}
                        inputId={index}
                        onChange={(e) => handleSubTaskCheckBox(e.checked, index)}
                        className="pointer h-auto me-2"
                        disabled={isLocked}
                      />
                      <span key={index} style={{ width: '88%' }} title={subItem?.sub_task_description} className="text-overflow-ellipsis">
                        {subItem?.sub_task_description}
                      </span>
                    </div>
                    <AvatarGroup>
                      {subItem?.assign_to?.map((user, i) => {
                        if (i < 4) {
                          // Use stable ID for subtask avatars
                          const stableId = `${item.task_id}-subtask-${index}-${user?.assignee_id || user?.group_id || i}`;
                          return (
                            <ImageComponent
                              key={stableId}
                              filePath={
                                user?.contact_id || user?.firm_user_id
                                  ? `${tenantId}/${user?.contact_id ? user?.contact_id : user?.firm_user_id}/profile_image`
                                  : null
                              }
                              fileName={user.first_name || user?.group_name}
                              className={`me-1 task-avatar assignee-${stableId}`}
                              style={AVATAR_STYLE}
                              // onClick={() => user?.group_status !== 'Deleted' && navigateToModule(user)}
                              toolTipTarget={`.assignee-${stableId}`}
                              // toolTipContent={user?.assignee_name || GroupUserTooltip(user, item?.assign_to)}
                              toolTipContent={
                                <>
                                  {user?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                                  {user?.assignee_name || GroupUserTooltip(user, item?.assign_to)}
                                </>
                              }
                              tooltipPosition="top"
                              showTooltip={true}
                            />
                          );
                        }
                        return null;
                      })}
                      {subItem?.assign_to?.length > 4 && (
                        <Avatar
                          label={`+${subItem?.assign_to?.length - 4}`}
                          className="me-1 pointer"
                          style={OVERFLOW_AVATAR_STYLE}
                          shape="circle"
                        />
                      )}
                    </AvatarGroup>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom comparison function for optimal memoization
// Only rerender if task data actually changes, ignore index/position changes
export default React.memo(TaskCard, (prevProps, nextProps) => {
  // If task ID changed, always rerender (different task)
  if (prevProps.item.task_id !== nextProps.item.task_id) {
    return false;
  }

  // If column changed (task moved), rerender
  if (prevProps.columnId !== nextProps.columnId) {
    return false;
  }

  // If lock state changed, rerender
  if (prevProps.isLocked !== nextProps.isLocked) {
    return false;
  }

  // Ignore index changes (react-window handles positioning)
  // Index changes happen during drag/drop but don't affect rendering

  // Deep check task properties that affect rendering
  const prevItem = prevProps.item;
  const nextItem = nextProps.item;

  // Check key task properties that affect display
  const taskPropsEqual =
    prevItem.task_name === nextItem.task_name &&
    prevItem.task_status === nextItem.task_status &&
    prevItem._task_stage_ === nextItem._task_stage_ &&
    prevItem.priority === nextItem.priority &&
    prevItem.due_date === nextItem.due_date &&
    prevItem.case_name === nextItem.case_name &&
    prevItem.case_id === nextItem.case_id &&
    prevItem.is_lead === nextItem.is_lead;

  if (!taskPropsEqual) {
    return false;
  }

  // Check assignees array
  const assigneesEqual =
    prevItem.assign_to?.length === nextItem.assign_to?.length &&
    prevItem.assign_to?.every((user, i) => user.assignee_id === nextItem.assign_to?.[i]?.assignee_id);

  if (!assigneesEqual) {
    return false;
  }

  // Check subtasks array (length and completion status)
  const subtasksEqual =
    prevItem.sub_task?.length === nextItem.sub_task?.length &&
    prevItem.sub_task?.every((st, i) => {
      const nextSt = nextItem.sub_task?.[i];
      return (
        st.task_id === nextSt?.task_id &&
        st.sub_task_status === nextSt?.sub_task_status &&
        st.sub_task_description === nextSt?.sub_task_description
      );
    });

  if (!subtasksEqual) {
    return false;
  }

  // If all checks pass, prevent rerender (return true)
  return true;
});
